import { z } from "zod";
import { ORGANIZATION_INTEGRATION_STATUS_ORDER } from "../helpers/organizationIntegrationStatus";
import { filterableConnectionProviderSchema } from "./organization-unified-connection.schema";

const [firstStatus, ...restStatuses] = ORGANIZATION_INTEGRATION_STATUS_ORDER;

/**
 * Derived from the status resolver's own value set so a new status can't be
 * accepted by the resolver while this filter enum silently 400s it.
 */
export const organizationIntegrationStatusSchema = z.enum([
    firstStatus,
    ...restStatuses,
]);

// Query-string serialization collapses a single-element array to a scalar, so
// wrap a lone value back into an array (mirrors InvoiceStatusArraySchema).
const scalarTolerantArray = <TSchema extends z.ZodTypeAny>(schema: TSchema) =>
    z.preprocess(
        (value) => (typeof value === "string" ? [value] : value),
        z.array(schema),
    );

/**
 * Optional search/filter set for the org-integrations group endpoints. Applied
 * server-side because both the group list and each group's items are paginated —
 * filtering in the client would only ever see the loaded slice.
 */
// Bounded like `search` is: repeated `statuses=`/`providers=` params bypass qs's
// index arrayLimit, so without a cap a caller could post an arbitrarily long
// array of valid enum values straight into the filter. One entry per enum value
// is all any legitimate client sends.
const STATUS_FILTER_LIMIT = ORGANIZATION_INTEGRATION_STATUS_ORDER.length;
const PROVIDER_FILTER_LIMIT = filterableConnectionProviderSchema.options.length;

export const organizationConnectionFiltersSchema = z.object({
    search: z.string().trim().max(200).optional(),
    statuses: scalarTolerantArray(organizationIntegrationStatusSchema)
        .refine((values) => values.length <= STATUS_FILTER_LIMIT)
        .optional(),
    providers: scalarTolerantArray(filterableConnectionProviderSchema)
        .refine((values) => values.length <= PROVIDER_FILTER_LIMIT)
        .optional(),
});

export type OrganizationConnectionFilters = z.infer<
    typeof organizationConnectionFiltersSchema
>;
